import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { ChangelogChange, ChangelogEntry } from '../../data/changelog';
import { tokens } from '../../theme/tokens';

const EASE = [0.16, 1, 0.3, 1] as const;

// Colored type tag shared visual language: feature => orange, improvement =>
// muted/info, fix => green. Kept as a leaf helper so ReleaseHistoryDialog can
// mirror the same look.
export const TYPE_TAG: Record<ChangelogChange['type'], { label: string; color: string; bg: string }> = {
  feature: { label: 'New', color: tokens.orange, bg: 'rgba(220, 88, 41, 0.10)' },
  improvement: { label: 'Improved', color: tokens.muted, bg: 'rgba(107, 98, 91, 0.10)' },
  fix: { label: 'Fixed', color: '#2E7D32', bg: 'rgba(46, 125, 50, 0.10)' },
};

const TYPE_ORDER: ChangelogChange['type'][] = ['feature', 'improvement', 'fix'];

export const TypeTag: React.FC<{ type: ChangelogChange['type'] }> = ({ type }) => {
  const tag = TYPE_TAG[type];
  return (
    <Chip
      size="small"
      label={tag.label}
      sx={{
        color: tag.color,
        bgcolor: tag.bg,
        fontWeight: 700,
        fontSize: '0.62rem',
        letterSpacing: '0.02em',
        height: 20,
        borderRadius: '6px',
        // Fixed width so every description starts at the same x — a ragged tag
        // column (New / Improved / Fixed differ in width) is what made the list
        // look untidy. The label is centered within the fixed box.
        width: 62,
        flexShrink: 0,
        '& .MuiChip-label': { px: 0, width: '100%', textAlign: 'center', overflow: 'visible' },
      }}
    />
  );
};

/**
 * Renders an entry's changes grouped by type with colored tags. Shared visual
 * building block reused by ReleaseHistoryDialog.
 */
export const ChangeList: React.FC<{ changes: ChangelogChange[] }> = ({ changes }) => {
  return (
    <Stack spacing={1}>
      {TYPE_ORDER.map((type) => {
        const items = changes.filter((c) => c.type === type);
        if (items.length === 0) return null;
        return (
          <Box key={type}>
            {items.map((change, idx) => (
              <Box
                key={idx}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.6 }}
              >
                {/* Tag sits in a box whose height matches the text's first-line box
                    (fontSize 0.875rem * lineHeight 1.55 ≈ 21.7px) and centers the 20px
                    chip within it, so the tag is vertically centered on the FIRST line
                    of the description — stable whether the text wraps or not. */}
                <Box
                  sx={{
                    flexShrink: 0,
                    height: 'calc(0.875rem * 1.55)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <TypeTag type={type} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{ flex: 1, minWidth: 0, color: tokens.ink, lineHeight: 1.55, fontSize: '0.875rem' }}
                >
                  {change.text}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      })}
    </Stack>
  );
};

interface WhatsNewDialogProps {
  open: boolean;
  entries: ChangelogEntry[];
  onClose: () => void;
  onSeeFullHistory: () => void;
}

// NOTE: This component does NOT decide when to appear and does NOT write
// localStorage (e.g. lastSeenVersion). The OnboardingOrchestrator owns that.
const WhatsNewDialog: React.FC<WhatsNewDialogProps> = ({ open, entries, onClose, onSeeFullHistory }) => {
  const reduceMotion = useReducedMotion();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: tokens.ink }}>What's New</DialogTitle>
      <DialogContent dividers>
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
        >
          <Stack spacing={2.5}>
            {entries.map((entry) => (
              <Box key={entry.version}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: tokens.ink }}>
                    v{entry.version}
                  </Typography>
                  <Typography variant="caption" sx={{ color: tokens.muted }}>
                    {entry.date}
                  </Typography>
                </Box>
                {entry.title && (
                  <Typography variant="body2" sx={{ color: tokens.muted, mb: 1.25 }}>
                    {entry.title}
                  </Typography>
                )}
                <ChangeList changes={entry.changes} />
              </Box>
            ))}
          </Stack>
        </motion.div>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onSeeFullHistory} color="inherit" sx={{ color: tokens.muted }}>
          See full history
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ bgcolor: tokens.orange, '&:hover': { bgcolor: tokens.orangeDark } }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsNewDialog;
